/**
 * Map utility functions & constants extracted from IndiaMap.jsx.
 * Contains: projection scales, colour scale, lookup builders.
 */
import { scaleQuantile } from "d3-scale";
import { normalizeGeoName, DISTRICT_DB_TO_TOPO, BLOCK_DB_TO_GEO } from "../data/geoMappings";

// ─── TopoJSON / GeoJSON URLs ─────────────────────────────────────────────────
export const STATES_URL     = "/maps/india-states-simple.topojson";
export const DISTRICTS_URL  = "/maps/india-districts-simple.topojson";
export const BLOCKS_URL_TPL = (normState) => `/maps/states/${normState.replace(/ /g, '_').toLowerCase()}-blocks.geojson`;

export const INDIA_CENTER = [82.7, 23.0];
export const INDIA_SCALE  = 830;

/**
 * Projection scales per state — computed from Mercator geometry.
 * scale = min(780 / lon_span_rad, 440 / mercator_lat_span)
 * Guarantees the state fits within an 820×480 canvas with ~40px padding.
 */
export const STATE_SCALES = {
  "Andhra Pradesh":     3312,
  "Arunachal Pradesh":  7705,
  "Assam":              5961,
  "Bihar":              7085,
  "Chhattisgarh":       3796,
  "Delhi":              44246,
  "Goa":                27011,
  "Gujarat":            5065,
  "Haryana":            6868,
  "Himachal Pradesh":   7650,
  "Jammu And Kashmir":  4832,
  "Jharkhand":          6793,
  "Karnataka":          3578,
  "Kerala":             5506,
  "Ladakh":             5973,
  "Madhya Pradesh":     3968,
  "Maharashtra":        3668,
  "Manipur":            12713,
  "Meghalaya":          14896,
  "Mizoram":            8911,
  "Nagaland":           11919,
  "Odisha":             4927,
  "Puducherry":         89381,
  "Punjab":             7201,
  "Rajasthan":          3127,
  "Sikkim":             22340,
  "Tamil Nadu":         4499,
  "Telangana":          5995,
  "Tripura":            14426,
  "Uttar Pradesh":      3448,
  "Uttarakhand":        7788,
  "West Bengal":        4097,
};

// ─── Colour scale ────────────────────────────────────────────────────────────
// Quantile-based bucketing with 35-step sequential "Reds" palette.
export const HEAT_PALETTE = [
  "#fff5f0", "#fef0e8", "#feebe1", "#fee6da", "#fee1d3", "#fdd9c9", "#fdd0bd",
  "#fcc8b2", "#fcbfa6", "#fcb69b", "#fcac90", "#fca285", "#fc997a", "#fb8f6f",
  "#fb8666", "#fb7c5c", "#fb7353", "#fb6a4a", "#f85e42", "#f5533b", "#f24834",
  "#ef3d2d", "#e83429", "#e02c25", "#d72422", "#cf1c1e", "#c6161c", "#bd141a",
  "#b41218", "#ab1016", "#a10e14", "#920a12", "#840710", "#75030e", "#67000d",
];

export function makeScale(lookup) {
  const vals = Object.values(lookup).filter(v => v > 0);
  if (!vals.length) return () => "#F1F5F9";
  const scale = scaleQuantile().domain(vals).range(HEAT_PALETTE);
  return (v) => (v > 0 ? scale(v) : "#E2E8F0");
}

// ─── Lookup builders ─────────────────────────────────────────────────────────
export function buildStateLookup(stateData) {
  const m = {};
  stateData?.forEach(d => { m[normalizeGeoName(d.state)] = d.allocation_cr || 0; });
  return m;
}

export function buildStateDataMap(stateData) {
  const m = {};
  stateData?.forEach(d => { m[normalizeGeoName(d.state)] = d; });
  return m;
}

export function buildDistrictLookup(districtData) {
  const m = {};
  districtData?.forEach(d => {
    const rawKey  = (d.district || "").toLowerCase().trim();
    const topoName = DISTRICT_DB_TO_TOPO.hasOwnProperty(rawKey)
      ? DISTRICT_DB_TO_TOPO[rawKey] : d.district;
    if (!topoName) return;
    const k = normalizeGeoName(topoName);
    m[k] = (m[k] || 0) + (d.allocation_cr || 0);
  });
  return m;
}

export function buildAllDistrictLookup(allDistrictData) {
  const m = {};
  allDistrictData?.forEach(d => {
    const rawKey  = (d.district || "").toLowerCase().trim();
    const topoName = DISTRICT_DB_TO_TOPO.hasOwnProperty(rawKey)
      ? DISTRICT_DB_TO_TOPO[rawKey] : d.district;
    if (!topoName) return;
    const stateKey = (d.state || "").toLowerCase().trim();
    const k = `${stateKey}|${normalizeGeoName(topoName)}`;
    m[k] = (m[k] || 0) + (d.allocation_cr || 0);
  });
  return m;
}

export function buildAllDistrictDataMap(allDistrictData) {
  const m = {};
  allDistrictData?.forEach(d => {
    const rawKey  = (d.district || "").toLowerCase().trim();
    const topoName = DISTRICT_DB_TO_TOPO.hasOwnProperty(rawKey)
      ? DISTRICT_DB_TO_TOPO[rawKey] : d.district;
    if (!topoName) return;
    const stateKey = (d.state || "").toLowerCase().trim();
    const k = `${stateKey}|${normalizeGeoName(topoName)}`;
    if (!m[k]) m[k] = d;
  });
  return m;
}

export function buildBlockLookup(blockData) {
  const m = {};
  blockData?.forEach(d => {
    const rawKey  = (d.block || "").toLowerCase().trim();
    const geoName = BLOCK_DB_TO_GEO.hasOwnProperty(rawKey)
      ? BLOCK_DB_TO_GEO[rawKey] : d.block;
    const k = normalizeGeoName(geoName || "");
    if (k) m[k] = (m[k] || 0) + (d.allocation_cr || 0);
  });
  return m;
}

export function buildDistrictDataMap(districtData) {
  const m = {};
  districtData?.forEach(d => {
    const rawKey  = (d.district || "").toLowerCase().trim();
    const topoName = DISTRICT_DB_TO_TOPO.hasOwnProperty(rawKey)
      ? DISTRICT_DB_TO_TOPO[rawKey] : d.district;
    if (!topoName) return;
    const k = normalizeGeoName(topoName);
    if (!m[k]) m[k] = d;
  });
  return m;
}

export function buildBlockDataMap(blockData) {
  const m = {};
  blockData?.forEach(d => {
    const rawKey  = (d.block || "").toLowerCase().trim();
    const geoName = BLOCK_DB_TO_GEO.hasOwnProperty(rawKey)
      ? BLOCK_DB_TO_GEO[rawKey] : d.block;
    const k = normalizeGeoName(geoName || "");
    if (k && !m[k]) m[k] = d;
  });
  return m;
}
